// @flow
import React, {useMemo} from 'react';
import Box from "@mui/material/Box";
import {Grid, Tooltip, Typography, useMediaQuery, useTheme} from "@mui/material";
import useUserData from "../../../hooks/user/useUserData";
import {roundToDecimal} from "../../../helpers/helpers";
import './productPreview.css'
import {ClearOutlined} from "@mui/icons-material";
import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import {PAGE_ORDERS} from "../../../locales/pages/namespaces";
import useOrderAction from "../../../hooks/order/useOrderAction";
import {NumericFormat} from "react-number-format";

type Props = {};

type Dot = {
	DotYear?: string,
	Qty?: string | number,
	displayStock?: number | string,
	DiscountPCT?: string | number,
};

const ProductPreview = (props: Props) => {
	const {
		product: {
			id,
			alternativeCode,
			eanCode,
			description,
			priceWithPaymentDiscountWithEnvTaxAndItemTax,
			warehouses,
			totalValueWithQty,
			totalValuePerUnit
		},
		orderSubmitted,
		orderId
	} = props

	const theme = useTheme();
	const matches = useMediaQuery(theme.breakpoints.down('md'))
	const {t} = useTranslation(PAGE_ORDERS)
	const {removeProductFromOrder} = useOrderAction()
	const {warehouses: sortedWarehouses, thousandSeparator, decimalSeparator} = useUserData()
	const [openWh, setOpenWh] = React.useState<Record<string, boolean>>({});
	const mappedWarehouses = useMemo(() => (
		sortedWarehouses.map(warehouse => {
			const productWarehouse = warehouses?.find(f => f.id === warehouse.id)
			return {
				...productWarehouse,
				...warehouse
			}
		})
	), [sortedWarehouses, warehouses])
	
	const totalProductQuantity = useMemo(() => (
		mappedWarehouses.reduce((acc, warehouse) => (
			acc + warehouse.quantity
		), 0)
	), [mappedWarehouses])
	
	const total = useMemo(() => (
		orderSubmitted ? totalValueWithQty : roundToDecimal(parseFloat(totalProductQuantity * priceWithPaymentDiscountWithEnvTaxAndItemTax).toFixed(2), 2)
	), [orderSubmitted, priceWithPaymentDiscountWithEnvTaxAndItemTax, totalProductQuantity, totalValueWithQty])

	const whWithDots = React.useMemo(() => {
		const byWh = Array.isArray(mappedWarehouses)
			? mappedWarehouses
				.map(w => ({
					id: w.id,
					address: w.address,
					dots: Array.isArray(w.dots) ? [...w.dots] : [],
				}))
				.filter(w => Array.isArray(w.dots) && w.dots.length > 0)
				.map(w => ({
					...w,
					dots: [...w.dots].sort((a, b) =>
						String(b?.DotYear ?? '').localeCompare(String(a?.DotYear ?? ''))
					),
				}))
			: [];
		return byWh;
	}, [mappedWarehouses]);


	const toggleWh = (id: number | string) =>
		setOpenWh(prev => ({ ...prev, [String(id)]: !prev[String(id)] }));
	
	return (
			<Grid
			container
			sx={{
				background: '#FFFFFF',
				boxShadow: 'rgba(37, 91, 130, 0.25) 0 0.0625em 0.0625em, rgba(37, 91, 130, 0.25) 0 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0 0 0 1px inset',
				padding: {xs: 1, md: 0.8},
				// border: '1px solid #F5F5F5',
				marginBottom: 1,
				maxWidth: '1600px'
			}}
			className={'product-preview'}
		>
			{!orderSubmitted && (
				<Grid item className={'remove-product'}>
					<Tooltip title={t('Remove from cart')}>
						<Button
							variant="text"
							color={'error'}
							onClick={() => {
								const originalProductId = id.split("-")?.pop()
								if(originalProductId) removeProductFromOrder(orderId, originalProductId, id)
							}}
							size={'small'}
						>
							<ClearOutlined fontSize={'small'}/>
							{matches && (t('Remove from cart'))}
						</Button>
					</Tooltip>
				</Grid>
			)}
			<Grid item xs={12} md={2}>
				<Typography variant={'h5'} component={'div'} sx={{wordWrap: 'break-word'}}>
					{description}
				</Typography>
				<Typography variant={'body2'} component={'div'}>
					{t('EAN CODE')}: {eanCode}
				</Typography>
				<Typography variant={'body2'} component={'div'}>
					{t('Alternative Code')}: {alternativeCode}
				</Typography>
			</Grid>
			<Grid item xs={12} md={2}>
				<Box className={'preview-warehouses'} sx={{justifyContent: {xs: 'space-between', md: 'space-around'}}}>
					{mappedWarehouses.map(warehouse => {
						return (
							<Box className={'preview-warehouse'} key={warehouse.id}>
								<Typography variant={'body2'} component={'div'}>
									{warehouse.address}
								</Typography>
								<Typography variant={'body2'} component={'div'} fontWeight={'bold'}>
									{warehouse.quantity}
								</Typography>
							</Box>
						)
					})}
				</Box>
			</Grid>
			<Grid item xs={12} md={6} sx={{display:'flex',justifyContent:'space-between'}}>
				<Box>
					<Grid container pt={1}>
						<Grid item xs={8}>
							<Typography component={'div'} variant={'body2'}>
								{t('Final piece value')}
							</Typography>
						</Grid>
						<Grid item xs={4}>
							<Typography component={'div'} variant={'body2'} fontWeight={'bold'} textAlign={'right'}>
								<NumericFormat
									thousandSeparator={thousandSeparator}
									decimalSeparator={decimalSeparator}
									displayType="text"
									value={orderSubmitted ? roundToDecimal(totalValuePerUnit, 2) : priceWithPaymentDiscountWithEnvTaxAndItemTax}
								/> €
							</Typography>
						</Grid>
						<Grid item xs={8}>
							<Typography component={'div'} variant={'body2'}>
								{t('Final total value')}
							</Typography>
						</Grid>
						<Grid item xs={4}>
							<Typography component={'div'} variant={'body2'} fontWeight={'bold'} textAlign={'right'}>
								<NumericFormat
									thousandSeparator={thousandSeparator}
									decimalSeparator={decimalSeparator}
									displayType="text"
									value={total}
								/> €
							</Typography>
						</Grid>
					</Grid>
				</Box>
				<Box>
					{whWithDots.length > 0 && (
						<Box
							sx={{
								flex: '0 0 auto',
								border: '1px solid rgba(0,0,0,0.1)',
								borderRadius: 1,
								background: '#fafafa',
								fontSize: 11,
								lineHeight: 1.2,
								px: 1,
								py: 0.6,
								minWidth: 240,
								maxWidth: 260,
								overflow: 'hidden',
							}}
						>
							{/* Header */}
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									borderBottom: '1px solid rgba(0,0,0,0.1)',
									fontWeight: 600,
									mb: 0.5,
									pb: 0.2,
								}}
							>
								<Box>{t('Warehouse')}</Box>
								<Box>{t('')}</Box>
							</Box>

							{/* Tree: Αποθήκη -> Χρόνια */}
							{whWithDots.map((w, wi) => {
								const isOpen = !!openWh[String(w.id)];
								return (
									<Box
										key={w.id ?? wi}
										sx={{
											borderBottom:
												wi < whWithDots.length - 1
													? '1px dashed rgba(0,0,0,0.08)'
													: 'none',
											py: 0.25,
										}}
									>
										{/* Γραμμή αποθήκης */}
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												cursor: 'pointer',
												userSelect: 'none',
											}}
											onClick={() => toggleWh(w.id)}
											title={String(w.address ?? '')}
										>
											<Box
												sx={{
													maxWidth: 140,
													pr: 1,
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
												}}
											>
												{w.address ?? `#${w.id}`}
											</Box>
											<Box sx={{ fontWeight: 600 }}>{isOpen ? '−' : '+'}</Box>
										</Box>

										{/* Παιδιά: Χρόνια */}
										{isOpen && (
											<Box sx={{ mt: 0.25, pl: 1 }}>
												{/* Header εσωτερικού πίνακα */}
												<Box
													sx={{
														display: 'flex',
														justifyContent: 'space-between',
														opacity: 0.7,
														fontWeight: 600,
														mb: 0.25,
													}}
												>
													<Box sx={{ width: '33%' }}>{t('Year')}</Box>
													<Box sx={{ width: '33%', textAlign: 'center' }}>
														{t('Quantity')}
													</Box>
													<Box sx={{ width: '33%', textAlign: 'right' }}>
														{t('Extra Disc.')}
													</Box>
												</Box>

												{/* Rows */}
												{w.dots.map((d: any, di: number) => {
													const qty =
														d?.Qty ?? d?.Quantity ?? d?.Stock ?? d?.Available ?? '-';
													const discountNum = Number(d?.DiscountPCT) || 0;
													const discountDisplay =
														discountNum === 0 ? '-' : `${discountNum}%`;

													return (
														<Box
															key={`${w.id}-${d?.DotYear ?? 'unknown'}-${di}`}
															sx={{
																display: 'flex',
																justifyContent: 'space-between',
																py: 0.15,
															}}
														>
															<Box sx={{ width: '33%' }}>{d?.DotYear ?? ''}</Box>
															<Box sx={{ width: '33%', textAlign: 'center' }}>
																{qty}
															</Box>
															<Box sx={{ width: '33%', textAlign: 'right' }}>
																{discountDisplay}
															</Box>
														</Box>
													);
												})}
											</Box>
										)}
									</Box>
								);
							})}
						</Box>
					)}
				</Box>


			</Grid>
		</Grid>


	);
};

export default ProductPreview